import PropTypes from 'prop-types';
import React from 'react';
import axios from 'axios';
import get from 'lodash/get';

import Modal from '@lightspeed/cirrus-modal';
import CTSClient from '@lightspeed/cts-client';

import CommonData from 'app/src/data/CommonData';
import Decimal from 'app/src/data/base/Decimal';
import PaymentOperations from 'app/src/payments/PaymentOperations';
import Terminal from 'app/src/payments/terminals/Terminal';

import { TransactionState, PaymentMethod, CTS_CONTAINER_ID } from './ManualPaymentTypes';
import ManualPaymentHeader from './ManualPaymentHeader';
import ManualPaymentBody from './ManualPaymentBody';
import ManualPaymentFooter from './ManualPaymentFooter';

const GATEWAY_DOMAIN = {
  DEVELOPMENT: 'pay-service.test',
  STAGING: 'lightspeedapp-test.apigee.net/payments',
  PRODUCTION: 'lightspeedapp-prod.apigee.net/payments',
};

// statuses matching what would be returned by the API
const PaymentStatus = {
  PENDING: 'pending',
  REFUNDED: 'refunded',
  VOIDED: 'voided',
  APPROVED: 'approved',
  ERROR: 'error',
  DECLINED: 'declined',
  CANCELLED: 'cancelled',
  UNKNOWN: 'unknown',
};

class ManualPaymentModal extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      isOpen: true,
      transactionState: TransactionState.WAITING_TO_START,
      selectedTerminal: props.paymentProvider.getTerminal(
        CommonData.selectedRegister.get('ccTerminalID')
      ),
      selectedManualMethod: PaymentMethod.MANUAL_TERMINAL,
      payment: null,
      error: null,
      postalCode: props.postalCode || '',
      cardHolderName: props.cardHolderName || '',
      amount: props.balance,
      isManualAuthorized: false,
    };

    this.handleCloseModal = this.handleCloseModal.bind(this);
    this.handleStartPayment = this.handleStartPayment.bind(this);
    this.handleRetryPayment = this.handleRetryPayment.bind(this);
    this.handleCancel = this.handleCancel.bind(this);
    this.handleDone = this.handleDone.bind(this);
    this.handleEnterCardDetails = this.handleEnterCardDetails.bind(this);
    this.handleCancelWhileInProgress = this.handleCancelWhileInProgress.bind(this);
    this.handleBackToInProgress = this.handleBackToInProgress.bind(this);
    this.handleEnterKeyPress = this.handleEnterKeyPress.bind(this);
    this.handlePostalCodeChange = this.handlePostalCodeChange.bind(this);
    this.handleAmountChange = this.handleAmountChange.bind(this);
    this.handleManualMethodChange = this.handleManualMethodChange.bind(this);
    this.handleTerminalChange = this.handleTerminalChange.bind(this);
    this.handleCardHolderNameChange = this.handleCardHolderNameChange.bind(this);
  }

  /**
   * Handles closing the modal.  Since we have closing animations, we need to make
   * sure we wait for that to finish before we unmount otherwise we'll get an
   * exception thrown by React.
   */
  // eslint-disable-next-line react/sort-comp
  handleCloseModal() {
    this.setState(
      {
        isOpen: false,
      },
      () => {
        this.props.onHide();
      }
    );
  }

  handleAmountChange(amount) {
    this.setState({ amount });
  }

  handlePostalCodeChange(postalCode) {
    this.setState({ postalCode });
  }

  handleCardHolderNameChange(cardHolderName) {
    this.setState({ cardHolderName });
  }

  handleManualMethodChange(selectedManualMethod) {
    this.setState({ selectedManualMethod });
  }

  handleTerminalChange(selectedTerminal) {
    this.setState({ selectedTerminal });
  }

  handleStartPayment() {
    this.handleStartTransaction();
  }

  handleEnterCardDetails() {
    const { postalCode } = this.state;
    const { orderID, customerID, paymentProvider } = this.props;

    this.setState(
      {
        transactionState: TransactionState.ENTER_CARD_DETAILS,
        payment: null,
        error: null,
        // eslint-disable-next-line react/no-unused-state
        detailsShown: false,
      },
      () => {
        axios
          .post('/admin/payments/CtsJwt')
          .then(response => {
            const { jwt, url } = response.data;
            let environment = 'development';

            if (RegExp(GATEWAY_DOMAIN.PRODUCTION).test(url)) {
              environment = 'production';
            } else if (RegExp(GATEWAY_DOMAIN.STAGING).test(url)) {
              environment = 'staging';
            }

            CTSClient.renderCardFields({
              token: jwt,
              orderID,
              customerID,
              elementID: CTS_CONTAINER_ID,
              gatewayConfigID: get(paymentProvider, 'activeGateway.id'),
              environment,
              postalCode,
            }).then(ctsClient => {
              this.ctsClient = ctsClient;

              this.setState({ isManualAuthorized: true });
            });
          })
          .catch(() => {
            this.setState({ transactionState: TransactionState.FAILED });
          });
      }
    );
  }

  handleRetryPayment() {
    const { selectedManualMethod } = this.state;

    if (selectedManualMethod === PaymentMethod.MANUAL_TERMINAL) {
      this.handleStartTransaction();
    } else if (selectedManualMethod === PaymentMethod.MANUAL_POS) {
      this.handleEnterCardDetails();
    }
  }

  handleCancel() {
    this.handleCloseModal();
  }

  handleDone() {
    this.setState(
      {
        isOpen: false,
      },
      () => {
        this.props.onDone();
      }
    );
  }

  /**
   * Sets the modal state to the cancel while in progress state.
   */
  handleCancelWhileInProgress() {
    this.setState(() => ({
      transactionState: TransactionState.CANCEL_FROM_TERMINAL,
    }));
  }

  /**
   * Handles initiating a transaction
   */
  handleStartTransaction() {
    const { saleManager, paymentProvider } = this.props;
    // eslint-disable-next-line no-unused-vars
    const { amount, selectedTerminal, postalCode, cardToken, selectedManualMethod } = this.state;

    if (selectedManualMethod === PaymentMethod.MANUAL_TERMINAL) {
      this.setState(() => ({
        payment: null,
        transactionState: TransactionState.IN_PROGRESS,
        error: null,
        // eslint-disable-next-line react/no-unused-state
        detailsShown: false,
      }));

      this.handleTerminalPaymentOperation(
        PaymentOperations.doTerminalPaymentOperation({
          amount,
          terminal: selectedTerminal,
          saleManager,
          gatewayConfig: paymentProvider.activeGateway,
          isManualEntry: true,
        })
      );
    } else if (selectedManualMethod === PaymentMethod.MANUAL_POS) {
      this.handlePosPaymentOperation();
    }
  }

  /**
   * Sets the modal state to the in progress state.
   */
  handleBackToInProgress() {
    this.setState(() => ({
      transactionState: TransactionState.IN_PROGRESS,
    }));
  }

  handlePosPaymentOperation() {
    const { saleManager, paymentProvider } = this.props;
    const { amount, cardHolderName } = this.state;
    const reportGroup = `RetailPOS_${window.merchantos.session.getSystemCustomerID()}`;
    const transactionId = saleManager.sale.id;

    this.ctsClient.generateToken(reportGroup, transactionId).then(successResponse => {
      const { token, error, expMonth, expYear, postalCode } = successResponse;

      if (!error) {
        // cleanup the the CTSClient rendered stuff so that React doesn't get confused
        this.ctsClient.removeCardFields();

        this.setState(() => ({
          payment: null,
          transactionState: TransactionState.IN_PROGRESS,
          error: null,
          // eslint-disable-next-line react/no-unused-state
          detailsShown: false,
        }));

        this.handleTerminalPaymentOperation(
          PaymentOperations.doManualEntryTokenPayment(
            amount,
            saleManager,
            paymentProvider.activeGateway,
            {
              postalCode,
              cardHolderName,
              cardToken: token,
              expiryMonth: expMonth,
              expiryYear: expYear,
              orderId: saleManager.sale.id,
              invoiceId: saleManager.sale.id,
            }
          )
        );
      }
    });
  }

  /**
   * Handles the response from a terminal transaction.
   *
   * @param {Deferred.Promise} promise The promise which resolves/fails when the transaction is finished.
   */
  handleTerminalPaymentOperation(promise) {
    // eslint-disable-next-line no-unused-vars
    const { saleManager, paymentProvider } = this.props;
    const { selectedManualMethod } = this.state;

    if (selectedManualMethod === PaymentMethod.MANUAL_POS) {
      promise
        // eslint-disable-next-line no-unused-vars
        .done(({ payment = null, response, status = null }) => {
          switch (status) {
            case PaymentStatus.APPROVED:
              this.setState(() => ({
                payment,
                transactionState: TransactionState.COMPLETE,
              }));
              break;

            case PaymentStatus.DECLINED:
              this.setState(() => ({
                payment,
                transactionState: TransactionState.DECLINED,
              }));
              break;

            case PaymentStatus.ERROR:
              this.setState(() => ({
                payment,
                transactionState: TransactionState.ERROR,
                error: get(payment, 'serviceModel.paymentTransaction.attributes.message'),
              }));
              break;

            default:
              this.setState(() => ({
                payment,
                transactionState: TransactionState.ERROR,
                error: `An unexpected response was received from the server: ${status}`,
              }));
              break;
          }
        })
        .fail((response = null) => {
          this.setState(() => ({
            transactionState: TransactionState.ERROR,
            error: response ? response.message : 'An unexpected error has occurred',
          }));
        });
    } else {
      promise
        .done(response => {
          switch (response.response) {
            case Terminal.Response.APPROVED:
            case Terminal.Response.PAYMENT_ALREADY_RECONCILED:
              this.setState(() => ({
                payment: response.payment,
                transactionState: TransactionState.COMPLETE,
              }));
              break;

            default:
              this.setState(() => ({
                transactionState: TransactionState.ERROR,
                error: 'An unexpected error has occured',
              }));
              break;
          }
        })
        .fail(response => {
          switch (response.response) {
            case Terminal.Response.CANCELLED_FROM_TERMINAL:
            case Terminal.Response.CANCELLED:
              this.setState(() => ({
                payment: response.payment,
                transactionState: TransactionState.CANCELED,
              }));
              break;

            case Terminal.Response.DECLINED:
              this.setState(() => ({
                payment: response.payment,
                transactionState: TransactionState.DECLINED,
              }));
              break;

            case Terminal.Response.ERROR:
            case Terminal.Response.TERMINAL_ERROR:
              const message =
                get(response, 'payment.serviceModel.paymentTransaction.parameters.message') ||
                response.error;

              const detailedErrorMessage =
                get(
                  response,
                  'payment.serviceModel.paymentTransaction.parameters.detailedMessage'
                ) || null;

              this.setState(() => ({
                payment: response.payment,
                transactionState: TransactionState.ERROR,
                error: detailedErrorMessage ? `${message} - ${detailedErrorMessage}` : message,
              }));
              break;

            default:
              this.setState(() => ({
                transactionState: TransactionState.ERROR,
                error: 'An unknown error has occurred.',
              }));
              break;
          }
        });
    }
  }

  getOutstandingBalance(amount) {
    return this.props.balance.minus(amount);
  }

  /**
   * Called by React when the Modal is mounted.
   */
  componentDidMount() {
    window.addEventListener('keypress', this.handleEnterKeyPress);
  }

  /**
   * Called by React when the Modal is unmounted
   */
  componentWillUnmount() {
    window.removeEventListener('keypress', this.handleEnterKeyPress);
  }

  /**
   * Called when an enter key is pressed
   *
   * @param {DOMEvent} event The triggering event
   */
  handleEnterKeyPress(event) {
    if (event.key === 'Enter') {
      const { selectedTerminal, transactionState } = this.state;

      switch (transactionState) {
        case TransactionState.WAITING_TO_START:
          if (selectedTerminal) {
            this.handleStartPayment();
          }
          break;

        case TransactionState.CANCELED:
        case TransactionState.DECLINED:
        case TransactionState.FAILED:
        case TransactionState.ERROR:
          this.handleRetryPayment();
          break;

        case TransactionState.COMPLETE:
          this.handleCloseModal();
          break;

        default:
          break;
      }
    }
  }

  /**
   * The React render method.
   *
   * @returns {node}  The rendered QuickPayCashModal
   */
  render() {
    const {
      isOpen,
      selectedTerminal,
      transactionState,
      selectedManualMethod,
      isManualAuthorized,
      postalCode,
      cardHolderName,
      error,
      payment,
    } = this.state;
    const { balance, amount, paymentProvider } = this.props;

    const cancelCallback =
      transactionState === TransactionState.IN_PROGRESS
        ? this.handleCancelWhileInProgress
        : this.handleCancel;

    const allowCloseWithEsc =
      transactionState !== TransactionState.IN_PROGRESS &&
      transactionState !== TransactionState.COMPLETE;

    // Provide a check if a merchant can switch between Manual POS and Manual Terminal
    // if they have more than one "manual-entry" option
    const hasMultipleManualPaymentMethod =
      paymentProvider.activeGatewaySupportsManualEntry &&
      paymentProvider.activeGatewaySupportsManualEntryFromTerminal;

    return (
      <Modal
        className="cirrus add-deposit"
        isOpen={isOpen}
        onRequestClose={this.handleCloseModal}
        closeOnEsc={allowCloseWithEsc}
        shouldCloseOnOverlayClick={false}
      >
        <ManualPaymentHeader
          selectedTerminal={selectedTerminal}
          transactionState={transactionState}
          onClose={this.handleCloseModal}
        />
        <ManualPaymentBody
          amount={amount}
          balance={balance}
          cardHolderName={cardHolderName}
          error={error}
          hasMultipleManualPaymentMethod={hasMultipleManualPaymentMethod}
          isManualAuthorized={isManualAuthorized}
          onAmountChange={this.handleAmountChange}
          onCardHolderNameChange={this.handleCardHolderNameChange}
          onManualMethodSelectedChange={this.handleManualMethodChange}
          onPostalCodeChange={this.handlePostalCodeChange}
          onTerminalSelectedChange={this.handleTerminalChange}
          payment={payment}
          paymentProvider={paymentProvider}
          postalCode={postalCode}
          activeGatewaySupportsManualEntry={paymentProvider.activeGatewaySupportsManualEntry}
          activeGatewaySupportsManualEntryFromTerminal={
            paymentProvider.activeGatewaySupportsManualEntryFromTerminal
          }
          transactionState={transactionState}
        />
        <ManualPaymentFooter
          selectedTerminal={selectedTerminal}
          areFieldsValid={cardHolderName.length > 0 && postalCode.length > 0}
          isManualAuthorized={isManualAuthorized}
          selectedManualMethod={selectedManualMethod}
          transactionState={transactionState}
          onCancel={cancelCallback}
          onOk={this.handleBackToInProgress}
          onStartPayment={this.handleStartPayment}
          onRetryPayment={this.handleRetryPayment}
          onEnterDetails={this.handleEnterCardDetails}
          onDone={this.handleDone}
        />
      </Modal>
    );
  }
}

ManualPaymentModal.propTypes = {
  onHide: PropTypes.func.isRequired,
  balance: PropTypes.instanceOf(Decimal).isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  paymentProvider: PropTypes.object.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  saleManager: PropTypes.object.isRequired,
};

ManualPaymentModal.defaultProps = {
  // eslint-disable-next-line react/default-props-match-prop-types
  selectedTerminal: null,
};

export default ManualPaymentModal;
